import { Grid} from "@mui/material"
import { push } from "connected-react-router"
import { crudGetList, EditContextProvider, ListContextProvider, ReactAdminComponentPropsWithId, ReduxState, ShowBase, useEditController, useListController, useShowContext } from "ra-core"
import { useRef, useEffect, useCallback, FC, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import BackupBasicInfoCard from "@/components/backups/BackupBasicInfoCard"
import BackupRecordsTable from "@/components/backups/BackupRecordsTable"
import BackupRetentionScheduleCard from "@/components/backups/BackupRetentionScheduleCard"
import BackupSchedulePlanCard from "@/components/backups/BackupSchedulePlanCard"
import BackupValidationScheduleCard from "@/components/backups/BackupValidationScheduleCard"
import { DatabaseInfoContextProvider } from "@/components/backups/DatabaseInfoContext"
import LogViewer, { LogViewerProvider, useLogViewerController } from "@/components/backups/LogViewer"
import { Content } from "@/components/content/Content"
import { nonSqlDbTypes } from "@/components/obfuscation/defs"
import CreateOrEdit from "@/controller/CreateOrEdit"
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers"
import { RecPatch } from "@/dataProvider/tools"
import { SaveActionSlim } from "@/dataProvider/types"
import { DatabaseResponseDto } from "@/gen/api/backup"
import { DeploymentResponseDto } from "@/gen/api/deploy"
import { OdResourcePageProps } from "@/types/resource"
import { decodeHashid, genUrlWithHashid } from "@/utils/hashid"
import { pages, sorts } from "@/utils/paramsWithGetMainList"
import { useResourceRegister } from "@/utils/resourceRegister"


export interface BackupValidationDetailProps {}


const BackupValidationDetail: FC<ReactAdminComponentPropsWithId> = (props) => {
  useResourceRegister({ name: 'backup-recs' })
  useResourceRegister({ name: 'backup-retention-days' })
  useResourceRegister({ name: 'upload-config-file' })
  useResourceRegister({ name: 'download-config-file' })
  useResourceRegister({ name: 'backup-schedule-type' })
  const { id: hId, ...rest } = props
  const id = decodeHashid(hId || '')

  return (
    <ShowBase id={id} {...rest} resource="databases" >
      <BackupValidationDetailCater {...{id}} {...rest} />
    </ShowBase>
  )
}

const BackupValidationDetailCater: FC<OdResourcePageProps> = (props) => {
  const { record } = useShowContext<DatabaseResponseDto & RecPatch>({ resource: 'databases', basePath: '' })
  const dispatch = useDispatch()

  const switchDatabase = useCallback((database: DatabaseResponseDto) => {
    dispatch(push(
      genUrlWithHashid(`/backups-n-validations/list/$0/show`, database.databaseId || 0, database.name)))
  }, [dispatch])
  
  const [databaseInfoValue, setDatabaseInfoValue] = useState({
    databaseInfo: record,
    deploymentInfo: undefined as DeploymentResponseDto|undefined,
    deploymentsAll: [] as DeploymentResponseDto[],
  })

  useSelector<ReduxState, any>(store => {
    const dRes = store.admin.resources['deployments/list']
    if (!dRes) { return }
    if (databaseInfoValue.databaseInfo?.isManaged 
    && (databaseInfoValue.deploymentsAll.length === 0)
    && dRes.list.loadedOnce) {
      const deploymentsAll = Object.values<DeploymentResponseDto&RecPatch>(dRes.data || {})
      const deploymentInfo = 
        deploymentsAll.find(d => d.databaseId === databaseInfoValue.databaseInfo?.databaseId)
      setTimeout(() => setDatabaseInfoValue(v => {
        if (v.deploymentInfo === deploymentInfo) { return v}
        return { ...v, deploymentInfo, deploymentsAll }
      }))
    }
  })

  useEffect(() => {
    setTimeout(() => setDatabaseInfoValue(v => {
      if (record === v.databaseInfo) { return v }
      const v1 = { ...v, databaseInfo: record }

      if (record?.isManaged) {
        if (v.deploymentsAll.length === 0) {
          dispatch(crudGetList('deployments/list', pages(), sorts(), {}))
          delete v1.deploymentInfo
        } else if (v.deploymentInfo?.databaseId !== record.databaseId) {
          v1.deploymentInfo = 
            v.deploymentsAll.find(d => d.databaseId === record.databaseId)
        }
      } else {
        delete v1.deploymentInfo
      }
      return v1
    }))
  }, [record, dispatch])


  return (
    <DatabaseInfoContextProvider value={databaseInfoValue}>
      <BackupValidationDetailBody 
        databaseInfo={record} 
        onSwitchDatabase={switchDatabase} 
        {...props} 
      />
    </DatabaseInfoContextProvider>
  )
}

export interface BackupValidationDetailBodyProps extends OdResourcePageProps {
  databaseInfo: DatabaseResponseDto | undefined;
  onSwitchDatabase: SaveActionSlim<DatabaseResponseDto>;
}


export const BackupValidationDetailBody: FC<BackupValidationDetailBodyProps> = ({
  databaseInfo,
  options,
  onSwitchDatabase,
  resource,
}) => {
  const pagesRef = useRef({ pages: pages(), databaseId: databaseInfo?.databaseId })
  const backupRecsListCtls = useListController({ resource: 'backup-recs', basePath: '', filter: { databaseId: databaseInfo?.databaseId } })
  pagesRef.current = { pages: { page: backupRecsListCtls.page, perPage: backupRecsListCtls.perPage }, databaseId: databaseInfo?.databaseId }
  const dispatch = useDispatch()

  useEffect(() => {
    const timer = setInterval(() => {
      pagesRef.current.pages.page === 1 && pagesRef.current.databaseId &&
        dispatch(crudGetList('backup-recs', pagesRef.current.pages, sorts(), { databaseId: pagesRef.current.databaseId }))
    }, 12000)
    return () => {
      timer && clearInterval(timer)
    }
  }, [])

  const logViewerCtrl = useLogViewerController()

  const valiScheduleEditCtrl = useEditController({ 
    id: databaseInfo?.databaseId, 
    resource: databaseInfo?.databaseId ? 'validation-schedule' : 'noop', 
    basePath: '', 
    mutationMode: 'pessimistic' })

  const handlers = useDefaultSavedHandlers("Update")

  const isNoSql = nonSqlDbTypes.has(databaseInfo?.databaseType!)

  return (
    <Content 
      pageName="Backup and Validation Details" 
      resourceName="Details" 
      breadcrumbs={options?.breadcrumbs && [...options.breadcrumbs, { label: 'Backup & Validation Jobs', path: '/backups-n-validations/list' }]}
    >
      <Grid container spacing={4}>
        <Grid
          item
          xs={12}
        >
          <BackupBasicInfoCard 
            preChosenDbId={databaseInfo?.databaseId || 0}
            onSwitchDatabase={onSwitchDatabase}
          />
        </Grid>
        {databaseInfo?.isManaged &&
          <Grid
            item
            xs={12}
          >
            <BackupSchedulePlanCard databaseId={databaseInfo.databaseId || 0} />
          </Grid>
        }
        {!isNoSql &&
          <>
            <Grid
              item
              xs={12}
            >
              <ListContextProvider value={backupRecsListCtls} >
                <LogViewerProvider value={logViewerCtrl}>
                  <BackupRecordsTable />
                  <LogViewer />
                </LogViewerProvider>
              </ListContextProvider>
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
            >
              <EditContextProvider value={valiScheduleEditCtrl}>
                <BackupValidationScheduleCard />
              </EditContextProvider>
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
            >
              <CreateOrEdit resource="databases" record={databaseInfo} basePath="" mutationMode="pessimistic"
                {...handlers}
              >
                <BackupRetentionScheduleCard />
              </CreateOrEdit>
            </Grid>
          </>}

      </Grid>
    </Content>
  )
}

export default BackupValidationDetail;
